import {
    type CaregiverResponseType,
    type ContactAttempt,
    type ContactAttemptOutreachStatus,
    type DataSourceType,
    type DeliveryMode,
    type DisplayOutreachStatus,
    type JoinedOutreachRead,
    type OutreachStatus,
    type OutreachStatusFilter,
    type ShiftAssignmentType,
    type ShiftStatus,
} from '@libraries/typescript/api_client';

type WellSkyOverrideReview = NonNullable<
    ContactAttempt['wellsky_override_review']
>;
type ShiftWellSkyOverrideReview =
    WellSkyOverrideReview['shift_reviews'][number];
type HHAExchangeOvertimeReview = NonNullable<
    ContactAttempt['hhaexchange_overtime_review']
>;
type ShiftHHAExchangeOvertimeReview =
    HHAExchangeOvertimeReview['shift_reviews'][number];
type OutreachMetadataWithShiftStatus = {
    readonly filled_shifts?: Record<string, unknown>;
    readonly cancelled_shifts?: Array<string>;
    readonly interested_shift_ids?: Array<string>;
};

const FILLED_OR_ASSIGNED_SHIFT_STATUSES: ReadonlySet<ShiftStatus> = new Set([
    'assigned',
    'assigned_and_confirmed',
    'in_progress',
    'completed',
]);
type RequiredWellSkyOverrideFlags = {
    readonly hasOvertimeError: boolean;
    readonly hasAuthorizationLimitError: boolean;
    readonly hasAuthorizationTimeError: boolean;
    readonly hasConflictError: boolean;
};
type SelectedWellSkyOverrideFlags = {
    readonly overrideOvertime: boolean;
    readonly overrideAuthorizationLimit: boolean;
    readonly overrideAuthorizationTime: boolean;
    readonly overrideConflicts: boolean;
};
type AssignAndFillShiftResponse = CaregiverResponseType | 'interested';
export type AssignAndFillCandidateSource = {
    readonly shiftResponses?:
        | Readonly<Record<string, AssignAndFillShiftResponse>>
        | null
        | undefined;
    readonly wellskyOverrideReview?:
        | ContactAttempt['wellsky_override_review']
        | null;
};
export type AssignAndFillCandidate<
    TSource extends AssignAndFillCandidateSource,
> = {
    readonly source: TSource;
    readonly interestedShiftIds: Array<string>;
};

const REQUIRED_WELLSKY_OVERRIDE_LABELS = {
    overtime: 'Overtime Warning',
    authorizationLimit: 'Authorization Limit Exceeded',
    authorizationTime: 'Authorization Time Outside',
    conflict: 'Scheduling Conflict',
} as const;

/**
 * WellSky and AxisCare both support propagating an assignment to the whole
 * forward recurring series in the EHR; other data sources only assign the
 * single materialized occurrence.
 */
export function supportsRecurringSeriesAssignment(
    dataSourceType: DataSourceType | null | undefined,
): boolean {
    return dataSourceType === 'wellsky' || dataSourceType === 'axiscare';
}

type ForwardSeriesShift = {
    readonly id: string;
    readonly shift_start_time: string;
    readonly external_id?: string | null;
};

/**
 * Returns the selected shifts when they form complete chronological tails
 * within their EHR series, or null when any selected series has a hole.
 * Grouped outreaches can contain several independent recurring series, so an
 * unselected series must not prevent assigning all future occurrences of a
 * selected one.
 */
export function getCompleteForwardSeriesTail<TShift extends ForwardSeriesShift>(
    shifts: ReadonlyArray<TShift>,
    selectedShiftIds: ReadonlySet<string>,
): Array<TShift> | null {
    if (selectedShiftIds.size === 0) {
        return null;
    }

    const orderedShifts = [...shifts].sort((left, right) => {
        const timeDifference =
            new Date(left.shift_start_time).getTime() -
            new Date(right.shift_start_time).getTime();
        return timeDifference || left.id.localeCompare(right.id);
    });
    const selectedShifts = orderedShifts.filter((shift) =>
        selectedShiftIds.has(shift.id),
    );
    if (selectedShifts.length !== selectedShiftIds.size) {
        return null;
    }

    // If a selected shift has not been linked to its EHR series, preserve the
    // previous whole-outreach rule. Treating an unknown identity as a unique
    // series would make any single selection look complete while the vendor
    // could still propagate it across unselected future occurrences.
    if (selectedShifts.some((shift) => !shift.external_id)) {
        const firstSelectedIndex = orderedShifts.findIndex((shift) =>
            selectedShiftIds.has(shift.id),
        );
        const tail = orderedShifts.slice(firstSelectedIndex);
        return tail.length === selectedShiftIds.size &&
            tail.every((shift) => selectedShiftIds.has(shift.id))
            ? tail
            : null;
    }

    const shiftsBySeries = new Map<string, Array<TShift>>();
    for (const shift of orderedShifts) {
        const seriesKey = shift.external_id
            ? `external:${shift.external_id}`
            : `unselected:${shift.id}`;
        const seriesShifts = shiftsBySeries.get(seriesKey) ?? [];
        seriesShifts.push(shift);
        shiftsBySeries.set(seriesKey, seriesShifts);
    }

    for (const seriesShifts of shiftsBySeries.values()) {
        const firstSelectedIndex = seriesShifts.findIndex((shift) =>
            selectedShiftIds.has(shift.id),
        );
        if (firstSelectedIndex < 0) {
            continue;
        }
        if (
            seriesShifts
                .slice(firstSelectedIndex)
                .some((shift) => !selectedShiftIds.has(shift.id))
        ) {
            return null;
        }
    }
    return selectedShifts;
}

/**
 * Default EHR assignment scope for a fill. A recurring-shift outreach is a
 * search for a permanent caregiver for the whole series, so on a
 * series-capable EHR it defaults to the whole forward series; every other
 * outreach type (callout coverage in particular) stays scoped to the single
 * occurrence so a one-off fill never takes over the series (PHO-14378).
 */
export function getDefaultAssignmentType({
    dataSourceType,
    outreachType,
}: {
    readonly dataSourceType: DataSourceType | null | undefined;
    readonly outreachType: JoinedOutreachRead['outreach_type'] | undefined;
}): ShiftAssignmentType {
    return supportsRecurringSeriesAssignment(dataSourceType) &&
        outreachType === 'recurring_shift'
        ? 'all_future'
        : 'single';
}

/**
 * Determine if an outreach is active (not in a terminal state).
 * Active statuses: 'pending', 'in_progress', 'paused', 'awaiting_confirmation'
 */
export function isActiveOutreach(
    status: OutreachStatus | DisplayOutreachStatus | undefined,
): boolean {
    return (
        status === 'pending' ||
        status === 'in_progress' ||
        status === 'paused' ||
        status === 'awaiting_confirmation'
    );
}

export function getInterestedShiftIds(
    shiftResponses:
        | Readonly<Record<string, AssignAndFillShiftResponse>>
        | null
        | undefined,
    excludedShiftIds?: ReadonlySet<string>,
): Array<string> {
    if (!shiftResponses) {
        return [];
    }

    return Object.entries(shiftResponses)
        .filter(
            ([shiftId, response]) =>
                (response === 'interested' ||
                    response === 'available_interested') &&
                !excludedShiftIds?.has(shiftId),
        )
        .map(([shiftId]) => shiftId);
}

export function getAssignAndFillCandidate<
    TSource extends AssignAndFillCandidateSource,
>(
    source: TSource,
    resolvedShiftIds?: ReadonlySet<string>,
): AssignAndFillCandidate<TSource> | null {
    const interestedShiftIds = getInterestedShiftIds(
        source.shiftResponses,
        resolvedShiftIds,
    );
    if (interestedShiftIds.length === 0 && !source.wellskyOverrideReview) {
        return null;
    }

    return {
        source,
        interestedShiftIds,
    };
}

export function getAssignAndFillCandidates<
    TSource extends AssignAndFillCandidateSource,
>(
    sources: ReadonlyArray<TSource>,
    resolvedShiftIds?: ReadonlySet<string>,
): Array<AssignAndFillCandidate<TSource>> {
    return sources.flatMap((source) => {
        const candidate = getAssignAndFillCandidate(source, resolvedShiftIds);
        return candidate ? [candidate] : [];
    });
}

const RESPONSE_PRIORITY: Array<CaregiverResponseType> = [
    'available_interested',
    'unavailable',
    'available_not_interested',
    'not_a_fit',
    'no_response',
    'pending',
    'unknown',
];

const OVERRIDING_RESPONSES: Array<CaregiverResponseType> = [
    'available_interested',
    'unavailable',
    'available_not_interested',
    'not_a_fit',
];

function isOverridingResponse(
    response: CaregiverResponseType | null,
): response is CaregiverResponseType {
    return response !== null && OVERRIDING_RESPONSES.includes(response);
}

function parseTimestamp(value: string | null | undefined): number | null {
    if (!value) {
        return null;
    }

    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
}

function compareTimestampsNewestFirst(
    first: number | null,
    second: number | null,
): number {
    if (first === null && second === null) {
        return 0;
    }
    if (first === null) {
        return 1;
    }
    if (second === null) {
        return -1;
    }
    return second - first;
}

export function sortAttemptsNewestFirst<
    T extends {
        created_at?: string | null;
    },
>(attempts: Array<T>): Array<T> {
    return attempts
        .map((attempt, index) => ({
            attempt,
            index,
            timestamp: parseTimestamp(attempt.created_at),
        }))
        .sort((a, b) => {
            if (a.timestamp === null && b.timestamp === null) {
                return a.index - b.index;
            }
            if (a.timestamp === null) {
                return 1;
            }
            if (b.timestamp === null) {
                return -1;
            }
            return b.timestamp - a.timestamp;
        })
        .map((entry) => entry.attempt);
}

function getResolvedResponseFromNewestFirst(
    responses: Array<CaregiverResponseType>,
): CaregiverResponseType | null {
    const latestResponse = responses[0] ?? null;
    if (!latestResponse) {
        return null;
    }

    if (isOverridingResponse(latestResponse)) {
        return latestResponse;
    }

    for (let i = 1; i < responses.length; i++) {
        const response = responses[i] ?? null;
        if (isOverridingResponse(response)) {
            return response;
        }
    }

    return latestResponse;
}

/**
 * Get the highest priority response from an array of responses.
 */
export function getHighestPriorityResponse(
    responses: Array<CaregiverResponseType>,
): CaregiverResponseType | null {
    for (const priority of RESPONSE_PRIORITY) {
        if (responses.includes(priority)) {
            return priority;
        }
    }
    return null;
}

/**
 * Get the overall response from a shift_responses record.
 */
export function getOverallResponseFromShiftResponses(
    shiftResponses: Record<string, CaregiverResponseType> | null | undefined,
): CaregiverResponseType | null {
    if (!shiftResponses) {
        return null;
    }
    const responses = Object.values(shiftResponses);
    if (responses.length === 0) {
        return null;
    }
    return getHighestPriorityResponse(responses);
}

/**
 * Resolve shift responses across contact attempts for each shift independently.
 * A more recently recorded response only overrides an older response when it
 * is decisive: available, unavailable, or not interested.
 */
export function getResolvedShiftResponsesFromContactAttempts<
    T extends {
        created_at?: string | null;
        shift_responses?: Record<string, CaregiverResponseType> | null;
        shift_response_recorded_at?: Record<string, string> | null;
    },
>(attempts: Array<T>): Record<string, CaregiverResponseType> | null {
    if (attempts.length === 0) {
        return null;
    }

    const responsesByShift = new Map<
        string,
        Array<{
            response: CaregiverResponseType;
            responseTimestamp: number | null;
            attemptTimestamp: number | null;
            attemptIndex: number;
        }>
    >();

    for (const [attemptIndex, attempt] of attempts.entries()) {
        for (const [shiftId, response] of Object.entries(
            attempt.shift_responses ?? {},
        )) {
            const responses = responsesByShift.get(shiftId) ?? [];
            responses.push({
                response,
                responseTimestamp:
                    parseTimestamp(
                        attempt.shift_response_recorded_at?.[shiftId],
                    ) ?? parseTimestamp(attempt.created_at),
                attemptTimestamp: parseTimestamp(attempt.created_at),
                attemptIndex,
            });
            responsesByShift.set(shiftId, responses);
        }
    }

    if (responsesByShift.size === 0) {
        return null;
    }

    const resolved: Record<string, CaregiverResponseType> = {};
    for (const [shiftId, responseEntries] of responsesByShift.entries()) {
        responseEntries.sort((a, b) => {
            const responseTimestampDifference = compareTimestampsNewestFirst(
                a.responseTimestamp,
                b.responseTimestamp,
            );
            if (responseTimestampDifference !== 0) {
                return responseTimestampDifference;
            }

            const attemptTimestampDifference = compareTimestampsNewestFirst(
                a.attemptTimestamp,
                b.attemptTimestamp,
            );
            if (attemptTimestampDifference !== 0) {
                return attemptTimestampDifference;
            }

            return a.attemptIndex - b.attemptIndex;
        });
        const response = getResolvedResponseFromNewestFirst(
            responseEntries.map((entry) => entry.response),
        );
        if (response !== null) {
            resolved[shiftId] = response;
        }
    }

    return Object.keys(resolved).length > 0 ? resolved : null;
}

export function getOutstandingWellSkyOverrideReviewFromContactAttempts<
    T extends {
        created_at?: string | null;
        shift_responses?: Record<string, CaregiverResponseType> | null;
        wellsky_override_review?:
            | ContactAttempt['wellsky_override_review']
            | null;
    },
>(
    attempts: Array<T>,
    options?: {
        filledShiftIds?: ReadonlySet<string>;
        cancelledShiftIds?: ReadonlySet<string>;
        assignedShiftIds?: ReadonlySet<string>;
    },
): WellSkyOverrideReview | null {
    if (attempts.length === 0) {
        return null;
    }

    const resolvedShiftResponses =
        getResolvedShiftResponsesFromContactAttempts(attempts) ?? {};
    const newestFirstAttempts = sortAttemptsNewestFirst(attempts);
    const filledShiftIds = options?.filledShiftIds;
    const cancelledShiftIds = options?.cancelledShiftIds;
    const assignedShiftIds = options?.assignedShiftIds;
    const seenShiftIds = new Set<string>();
    const shiftReviews: Array<ShiftWellSkyOverrideReview> = [];

    for (const attempt of newestFirstAttempts) {
        const wellskyOverrideReview = attempt.wellsky_override_review;
        if (wellskyOverrideReview?.type !== 'wellsky_override_required') {
            continue;
        }

        for (const shiftReview of wellskyOverrideReview.shift_reviews) {
            const shiftId = shiftReview.shift_id;
            if (seenShiftIds.has(shiftId)) {
                continue;
            }
            seenShiftIds.add(shiftId);
            if (filledShiftIds?.has(shiftId)) {
                continue;
            }
            if (cancelledShiftIds?.has(shiftId)) {
                continue;
            }
            if (assignedShiftIds?.has(shiftId)) {
                continue;
            }
            if (resolvedShiftResponses[shiftId] !== 'available_interested') {
                continue;
            }
            shiftReviews.push(shiftReview);
        }
    }

    if (shiftReviews.length === 0) {
        return null;
    }

    return {
        type: 'wellsky_override_required',
        shift_reviews: shiftReviews,
    };
}

export function getOutstandingHHAExchangeOvertimeReviewFromContactAttempts<
    T extends {
        created_at?: string | null;
        shift_responses?: Record<string, CaregiverResponseType> | null;
        hhaexchange_overtime_review?:
            | ContactAttempt['hhaexchange_overtime_review']
            | null;
    },
>(
    attempts: Array<T>,
    options?: {
        filledShiftIds?: ReadonlySet<string>;
        cancelledShiftIds?: ReadonlySet<string>;
        assignedShiftIds?: ReadonlySet<string>;
    },
): HHAExchangeOvertimeReview | null {
    if (attempts.length === 0) {
        return null;
    }

    const resolvedShiftResponses =
        getResolvedShiftResponsesFromContactAttempts(attempts) ?? {};
    const newestFirstAttempts = sortAttemptsNewestFirst(attempts);
    const filledShiftIds = options?.filledShiftIds;
    const cancelledShiftIds = options?.cancelledShiftIds;
    const assignedShiftIds = options?.assignedShiftIds;
    const seenShiftIds = new Set<string>();
    const shiftReviews: Array<ShiftHHAExchangeOvertimeReview> = [];

    for (const attempt of newestFirstAttempts) {
        const overtimeReview = attempt.hhaexchange_overtime_review;
        if (overtimeReview?.type !== 'hhaexchange_overtime_review_required') {
            continue;
        }

        for (const shiftReview of overtimeReview.shift_reviews) {
            const shiftId = shiftReview.shift_id;
            if (seenShiftIds.has(shiftId)) {
                continue;
            }
            seenShiftIds.add(shiftId);
            if (filledShiftIds?.has(shiftId)) {
                continue;
            }
            if (cancelledShiftIds?.has(shiftId)) {
                continue;
            }
            if (assignedShiftIds?.has(shiftId)) {
                continue;
            }
            if (resolvedShiftResponses[shiftId] !== 'available_interested') {
                continue;
            }
            shiftReviews.push(shiftReview);
        }
    }

    if (shiftReviews.length === 0) {
        return null;
    }

    return {
        type: 'hhaexchange_overtime_review_required',
        shift_reviews: shiftReviews,
    };
}

export function getMissingRequiredWellSkyOverrideLabels(
    requiredFlags: RequiredWellSkyOverrideFlags,
    selectedFlags: SelectedWellSkyOverrideFlags,
): Array<string> {
    const missingLabels: Array<string> = [];

    if (requiredFlags.hasOvertimeError && !selectedFlags.overrideOvertime) {
        missingLabels.push(REQUIRED_WELLSKY_OVERRIDE_LABELS.overtime);
    }

    if (
        requiredFlags.hasAuthorizationLimitError &&
        !selectedFlags.overrideAuthorizationLimit
    ) {
        missingLabels.push(REQUIRED_WELLSKY_OVERRIDE_LABELS.authorizationLimit);
    }

    if (
        requiredFlags.hasAuthorizationTimeError &&
        !selectedFlags.overrideAuthorizationTime
    ) {
        missingLabels.push(REQUIRED_WELLSKY_OVERRIDE_LABELS.authorizationTime);
    }

    if (requiredFlags.hasConflictError && !selectedFlags.overrideConflicts) {
        missingLabels.push(REQUIRED_WELLSKY_OVERRIDE_LABELS.conflict);
    }

    return missingLabels;
}

export function getShiftStatusBadgeStatus(
    outreach: JoinedOutreachRead,
    shiftId: string,
    shiftEndTime: string,
    shiftStatus?: ShiftStatus | null,
): DisplayOutreachStatus {
    const metadata =
        outreach.outreach_metadata as OutreachMetadataWithShiftStatus | null;

    const filledShiftIds = new Set(Object.keys(metadata?.filled_shifts ?? {}));
    if (filledShiftIds.has(shiftId)) {
        return 'filled';
    }

    const cancelledShiftIds = new Set(metadata?.cancelled_shifts ?? []);
    if (cancelledShiftIds.has(shiftId) || outreach.status === 'cancelled') {
        return 'cancelled';
    }

    if (outreach.status === 'filled') {
        return 'filled';
    }

    if (outreach.status === 'unfilled') {
        return 'unfilled';
    }

    if (new Date(shiftEndTime).getTime() < Date.now()) {
        return 'unfilled';
    }

    // SHIFT_MISSING surfaces at the row level so the coordinator knows
    // the linked shift is gone from the source EHR. All terminal
    // resolutions (filled / cancelled / unfilled) and past-end auto-
    // unfilled outcomes win above, mirroring home.py's
    // _compute_display_status. The per-row "Shift Missing" indicator
    // (rendered separately in outreach_list.tsx) covers the case where
    // the outreach has resolved but the linked shift is also missing.
    if (shiftStatus === 'missing') {
        return 'shift_missing';
    }

    const interestedShiftIds = new Set(metadata?.interested_shift_ids ?? []);
    if (interestedShiftIds.has(shiftId)) {
        return 'awaiting_confirmation';
    }

    if (outreach.status === 'pending') {
        return 'pending';
    }

    if (outreach.status === 'paused') {
        return 'paused';
    }

    return 'in_progress';
}

export function shiftMatchesOutreachStatusFilters(
    outreach: JoinedOutreachRead,
    shift: JoinedOutreachRead['shifts'][number],
    statusFilters: ReadonlyArray<OutreachStatusFilter>,
): boolean {
    if (statusFilters.length === 0) {
        return true;
    }

    const shiftBadgeStatus = getShiftStatusBadgeStatus(
        outreach,
        shift.id,
        shift.shift_end_time,
        shift.status,
    );
    const effectiveStatus =
        shiftBadgeStatus === 'shift_missing'
            ? outreach.status
            : shiftBadgeStatus;
    const metadata =
        outreach.outreach_metadata as OutreachMetadataWithShiftStatus | null;
    const hasInterestedResponse = (
        metadata?.interested_shift_ids ?? []
    ).includes(shift.id);
    const hasFilledOrAssignedSignal =
        Object.prototype.hasOwnProperty.call(
            metadata?.filled_shifts ?? {},
            shift.id,
        ) ||
        (shift.assigned_caregiver_id !== null &&
            shift.assigned_caregiver_id !== undefined) ||
        FILLED_OR_ASSIGNED_SHIFT_STATUSES.has(shift.status);
    // Keep rendering narrower than _outreach_has_filled_or_assigned_shift_condition.
    const matchesFilledShift =
        shiftBadgeStatus === 'filled' ||
        (hasFilledOrAssignedSignal &&
            !['unfilled', 'cancelled', 'shift_missing'].includes(
                shiftBadgeStatus,
            ));

    return statusFilters.some((statusFilter) => {
        if (statusFilter === 'has_yes') {
            return hasInterestedResponse;
        }
        if (statusFilter === 'filled_shift') {
            return matchesFilledShift;
        }
        if (statusFilter === 'in_progress') {
            return (
                effectiveStatus === 'in_progress' ||
                effectiveStatus === 'awaiting_confirmation'
            );
        }
        return effectiveStatus === statusFilter;
    });
}

export function hasReviewRequiredShift(outreach: JoinedOutreachRead): boolean {
    return outreach.shifts.some(
        (shift) =>
            getShiftStatusBadgeStatus(
                outreach,
                shift.id,
                shift.shift_end_time,
                shift.status,
            ) === 'awaiting_confirmation',
    );
}

/**
 * Get the overall caregiver response from an array of contact attempts.
 * A newer attempt only overrides older attempts when it is decisive:
 * available, unavailable, or not interested.
 */
export function getOverallCaregiverResponse<
    T extends {
        created_at?: string | null;
        shift_responses?: Record<string, CaregiverResponseType> | null;
    },
>(attempts: Array<T>): CaregiverResponseType | null {
    return getOverallResponseFromShiftResponses(
        getResolvedShiftResponsesFromContactAttempts(attempts),
    );
}

/**
 * Get the display status for an outreach, showing 'awaiting_confirmation'
 * if any caregiver's effective response is interested, even if backend status lags.
 */
export function getDisplayOutreachStatus<
    T extends {
        caregiver_id: string;
        created_at?: string | null;
        shift_responses?: Record<string, CaregiverResponseType> | null;
    },
>(
    backendStatus: OutreachStatus,
    contactAttempts: Array<T> | null | undefined,
): DisplayOutreachStatus {
    if (backendStatus !== 'in_progress') {
        return backendStatus;
    }

    const attemptsByCaregiver = new Map<string, Array<T>>();
    for (const attempt of contactAttempts ?? []) {
        const caregiverAttempts =
            attemptsByCaregiver.get(attempt.caregiver_id) ?? [];
        caregiverAttempts.push(attempt);
        attemptsByCaregiver.set(attempt.caregiver_id, caregiverAttempts);
    }

    const hasInterestedResponse = Array.from(attemptsByCaregiver.values()).some(
        (caregiverAttempts) =>
            getOverallCaregiverResponse(caregiverAttempts) ===
            'available_interested',
    );

    return hasInterestedResponse ? 'awaiting_confirmation' : backendStatus;
}

type ContactAttemptDetailsTextInput = {
    readonly delivery_mode: DeliveryMode;
    readonly outreach_status: ContactAttemptOutreachStatus;
    readonly sms_message_text?: string | null;
    readonly call_summary?: string | null;
    readonly transcript_text?: string | null;
};

function getVoiceAttemptFallbackText(
    status: ContactAttemptOutreachStatus,
): string {
    switch (status) {
        case 'queued':
            return 'Queued to call';
        case 'initiated':
            return 'Calling…';
        case 'in_progress':
            return 'Call in progress';
        case 'reviewing_call':
            return 'Reviewing call';
        case 'sent':
            return 'Calling…';
        case 'delivered':
            return 'Delivered';
        case 'completed':
            return 'No transcript available';
        case 'failed':
            return 'Call failed';
        case 'no_answer':
            return 'No answer';
        case 'unreachable':
            return 'Phone unavailable';
        case 'voicemail_left':
            return 'Voicemail left';
        case 'unable_to_leave_message':
            return 'Unable to leave message';
        case 'call_disconnected':
            return 'Complete';
        case 'live_answer':
            return 'Call answered';
        case 'manually_notified':
            return 'Manually notified';
        case 'transferred':
            return 'Call transferred';
        default:
            return '—';
    }
}

function getSmsAttemptFallbackText(
    status: ContactAttemptOutreachStatus,
): string {
    switch (status) {
        case 'queued':
            return 'Queued';
        case 'initiated':
            return 'Sending…';
        case 'in_progress':
            return 'In progress';
        case 'sent':
            return 'Sent';
        case 'delivered':
            return 'Delivered';
        case 'completed':
            return 'Completed';
        case 'failed':
            return 'Failed';
        case 'no_answer':
            return 'No response';
        case 'unreachable':
            return 'Phone unavailable';
        case 'manually_notified':
            return 'Manually notified';
        default:
            return '—';
    }
}

export function getContactAttemptDetailsText(
    attempt: ContactAttemptDetailsTextInput,
): string {
    if (attempt.delivery_mode === 'sms' && attempt.sms_message_text) {
        return attempt.sms_message_text;
    }

    if (attempt.call_summary) {
        return attempt.call_summary.length > 100
            ? `${attempt.call_summary.substring(0, 100)}...`
            : attempt.call_summary;
    }

    if (!attempt.transcript_text) {
        return attempt.delivery_mode === 'voice'
            ? getVoiceAttemptFallbackText(attempt.outreach_status)
            : getSmsAttemptFallbackText(attempt.outreach_status);
    }

    const userMatch = attempt.transcript_text.match(
        /(?:User|Caregiver):\s*(.+?)(?:\n|$)/,
    );
    const firstUserLine = userMatch?.[1]?.trim() ?? null;

    if (!firstUserLine) {
        return attempt.delivery_mode === 'voice'
            ? getVoiceAttemptFallbackText(attempt.outreach_status)
            : getSmsAttemptFallbackText(attempt.outreach_status);
    }

    return firstUserLine.length > 80
        ? `${firstUserLine.substring(0, 80)}...`
        : firstUserLine;
}

type RecommendationRequestError = {
    readonly status?: number;
    readonly message: string;
};

// The API reports a rejected filter as validation detail, so its text is the
// only signal that separates "too many filter values" from any other bad
// request. Matching narrowly keeps unrelated rejections readable.
const FILTER_TOO_LARGE_PATTERN =
    /cannot have more than \d+ children|exceeds max (?:depth|nodes|children)/i;

export function isRecommendationFilterTooLarge(
    error: RecommendationRequestError,
): boolean {
    return error.status === 422 && FILTER_TOO_LARGE_PATTERN.test(error.message);
}

// A size rejection arrives as a raw Pydantic detail string naming DSL paths
// ("filter.children.0: Value error, …"), which means nothing to a scheduler.
// Keep the raw message for every other failure.
export function recommendationErrorMessage(
    error: RecommendationRequestError,
): string {
    if (isRecommendationFilterTooLarge(error)) {
        return 'This filter combination is too large. Remove a few filter values to see results again.';
    }
    return error.message;
}
